import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Menu, X, LogOut, Plus, Briefcase, User, ChevronDown, Award } from 'lucide-react';
import UserProfile from '../components/dashboard/UserProfile';
import AddEntity from '../components/dashboard/AddEntity';
import MyBusinesses from '../components/dashboard/MyBusinesses';
import ClaimBusiness from './ClaimBusiness';
import { useAuth } from '../hooks/useAuth.js';
import { fetchUserProfile } from '../domain/useCases/User/fetchUserProfile.js';
import { fetchCurrentUser } from '../domain/useCases/User/fetchCurrentUser.js';
import { storageService } from '../domain/services/StorageService.js';

const Dashboard = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'profile');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showBackWarning, setShowBackWarning] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const fetchInitializedRef = useRef(false);
  const { logout } = useAuth();

  // Update activeTab when URL query params change
  useEffect(() => {
    const tabFromUrl = searchParams.get('tab');
    if (tabFromUrl) {
      setActiveTab(tabFromUrl);
    }
  }, [searchParams]);

  // Update URL when active tab changes
  useEffect(() => {
    const currentTab = searchParams.get('tab');
    if (activeTab !== currentTab) {
      const newSearchParams = new URLSearchParams();
      // Don't add tab param for profile and my-businesses to keep clean URLs
      if (activeTab !== 'profile' && activeTab !== 'my-businesses') {
        newSearchParams.set('tab', activeTab);
      }
      navigate(`${location.pathname}?${newSearchParams.toString()}`, { replace: true });
    }
  }, [activeTab, navigate, location.pathname]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setProfileDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Prevent back button navigation
  useEffect(() => {
    const handlePopState = (event) => {
      event.preventDefault();
      setShowBackWarning(true);
      // Push a new state to prevent actual back navigation
      window.history.pushState(null, null, window.location.href);
    };

    window.history.pushState(null, null, window.location.href);
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  useEffect(() => {
    const token = storageService.getAuthToken();
    if (!token) {
      navigate('/login');
      return;
    }

    if (fetchInitializedRef.current) return;
    fetchInitializedRef.current = true;

    fetchUserData();
  }, [navigate]);

  const fetchUserData = async () => {
    try {
      let data = await fetchCurrentUser();
      if (!data) {
        data = await fetchUserProfile();
      }
      setUserData(data);
    } catch (err) {
      setUserData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (err) {
      storageService.clearAuth();
    } finally {
      navigate('/');
    }
  };

  const isEntityRegistrationEnabled = import.meta.env.VITE_ENABLE_ENTITY_REGISTRATION === 'true';

  const navItems = [
    { id: 'profile', label: 'Profile', icon: User },
    ...(isEntityRegistrationEnabled ? [{ id: 'add-entity', label: 'Add Entity', icon: Plus }] : []),
    { id: 'my-businesses', label: 'My Businesses', icon: Briefcase },
    { id: 'claim-business', label: 'Claim Business', icon: Award },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-4 border-green-500/30 border-t-green-500 rounded-full animate-spin"></div>
          <p className="text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black">
      {/* Back Button Warning Modal */}
      {showBackWarning && (
        <div className="fixed inset-0 bg-black/70 z-[100] flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-gradient-to-br from-white/10 to-white/5 rounded-2xl shadow-2xl p-8 backdrop-blur-xl border border-white/20 max-w-md w-full">
            <h2 className="text-2xl font-bold text-white mb-4">Please Logout First</h2>
            <p className="text-gray-300 mb-6">
              You are currently logged in. Please logout from your account before leaving the dashboard.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowBackWarning(false)}
                className="flex-1 bg-white/10 hover:bg-white/20 text-white font-bold py-2 px-4 rounded-lg transition-all duration-300 border border-white/20"
              >
                Stay Here
              </button>
              <button
                onClick={() => {
                  setShowBackWarning(false);
                  handleLogout();
                }}
                className="flex-1 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-400 hover:to-red-500 text-white font-bold py-2 px-4 rounded-lg transition-all duration-300 shadow-lg hover:shadow-red-500/50"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-col md:flex-row h-screen">
        {/* Sidebar */}
        <div
          className={`fixed inset-y-0 left-0 z-40 md:relative md:inset-auto h-full bg-gradient-to-br from-gray-800/95 to-gray-900/95 backdrop-blur-xl border-r border-white/10 transition-all duration-300 transform ${mobileMenuOpen ? 'translate-x-0 w-64' : '-translate-x-full md:translate-x-0'}
            } ${sidebarOpen ? 'md:w-64' : 'md:w-20'} overflow-hidden`}
        >
          {/* Sidebar Header */}
          <div className="p-6 border-b border-white/10">
            <div className="flex items-center justify-between">
              {(mobileMenuOpen || sidebarOpen) && (
                <h2 className="text-xl font-bold bg-gradient-to-r from-green-400 to-orange-400 bg-clip-text text-transparent">
                  KAHA
                </h2>
              )}
              {/* Desktop Toggle Button - Only show on desktop */}
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="hidden md:flex text-gray-400 hover:text-white transition-colors"
              >
                {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
              {/* Mobile Close Button - Only show when sidebar is open on mobile */}
              {mobileMenuOpen && (
                <button
                  onClick={() => setMobileMenuOpen(false)}
                  className="md:hidden text-gray-400 hover:text-white transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              )}
            </div>
          </div>

          {/* Navigation Items */}
          <nav className="p-4 space-y-2">
            {navItems.map(item => {
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  onClick={() => {
                    setActiveTab(item.id);
                    setMobileMenuOpen(false);
                  }}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-300 ${activeTab === item.id
                    ? 'bg-gradient-to-r from-green-500/30 to-green-600/20 text-green-300 border border-green-500/30'
                    : 'text-gray-400 hover:text-white hover:bg-white/5'
                    }`}
                >
                  <Icon className="w-5 h-5 flex-shrink-0" />
                  {(mobileMenuOpen || sidebarOpen) && <span className="font-semibold">{item.label}</span>}
                </button>
              );
            })}
          </nav>

          {/* Logout Button */}
          <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-white/10 bg-gradient-to-t from-gray-900 to-transparent">
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-red-400 hover:bg-red-500/10 transition-all duration-300"
            >
              <LogOut className="w-5 h-5 flex-shrink-0" />
              {(mobileMenuOpen || sidebarOpen) && <span className="font-semibold">Logout</span>}
            </button>
          </div>
        </div>

        {/* Mobile Menu Button - Hide when sidebar is open */}
        {!mobileMenuOpen && (
          <button
            onClick={() => setMobileMenuOpen(true)}
            className="md:hidden fixed top-4 left-4 z-50 bg-green-500 hover:bg-green-600 text-white p-3 rounded-lg transition-all shadow-lg"
          >
            <Menu className="w-5 h-5" />
          </button>
        )}

        {/* Mobile Menu Overlay */}
        {mobileMenuOpen && (
          <div
            className="md:hidden fixed inset-0 bg-black/50 z-30"
            onClick={() => setMobileMenuOpen(false)}
          />
        )}

        {/* Main Content */}
        <div className="flex-1 overflow-auto">
          <div className="relative p-3 sm:p-4 md:p-6 lg:p-8">
            {/* Top Right User Profile Dropdown */}
            <div className="absolute top-3 right-3 sm:top-4 sm:right-4 md:top-6 md:right-6 lg:top-8 lg:right-8 z-50" ref={dropdownRef}>
              <button
                onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
                className="flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/30 rounded-full px-2 py-1.5 sm:px-3 sm:py-2 transition-all duration-300"
              >
                <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-gradient-to-br from-green-400 to-green-500 flex items-center justify-center overflow-hidden border-2 border-green-400 flex-shrink-0">
                  {userData?.avatar ? (
                    <img src={userData.avatar} alt="Avatar" className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-xs sm:text-xs font-bold text-white">
                      {userData?.firstName?.charAt(0)}{userData?.lastName?.charAt(0)}
                    </span>
                  )}
                </div>
                <ChevronDown className={`w-3 h-3 sm:w-4 sm:h-4 text-gray-400 transition-transform flex-shrink-0 ${profileDropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              {/* Dropdown Menu */}
              {profileDropdownOpen && (
                <div className="absolute top-full right-0 mt-2 sm:mt-3 bg-gradient-to-br from-gray-800 to-gray-900 border border-white/20 rounded-lg shadow-2xl overflow-hidden min-w-44 sm:min-w-48">
                  {/* User Info */}
                  <div className="px-4 py-3 border-b border-white/10">
                    <p className="text-white font-semibold text-sm whitespace-nowrap overflow-hidden text-ellipsis">
                      {`${userData?.firstName || ''} ${userData?.lastName || ''}`}
                    </p>
                    <p className="text-gray-400 text-xs truncate">{userData?.email}</p>
                  </div>

                  {/* Menu Items */}
                  <div className="py-2">
                    <button
                      onClick={() => {
                        setActiveTab('profile');
                        setProfileDropdownOpen(false);
                      }}
                      className="w-full flex items-center gap-3 px-4 py-2 text-gray-300 hover:bg-white/10 transition-colors text-sm"
                    >
                      <User className="w-4 h-4" />
                      Profile
                    </button>
                    <button
                      onClick={() => {
                        handleLogout();
                        setProfileDropdownOpen(false);
                      }}
                      className="w-full flex items-center gap-3 px-4 py-2 text-red-400 hover:bg-red-500/10 transition-colors text-sm"
                    >
                      <LogOut className="w-4 h-4" />
                      Logout
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Header - with margin top to avoid overlap */}
            <div className="mb-6 sm:mb-8 pt-16 sm:pt-20 md:pt-0">
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold bg-gradient-to-r from-white via-green-200 to-orange-300 bg-clip-text text-transparent mb-2">
                {navItems.find(item => item.id === activeTab)?.label}
              </h1>
              <p className="text-gray-400">
                {activeTab === 'add-entity' && 'Register a new business or entity'}
                {activeTab === 'my-businesses' && 'View and manage your businesses'}
                {activeTab === 'claim-business' && 'Search and claim businesses'}
              </p>
            </div>

            {/* Content Area */}
            <div className="bg-gradient-to-br from-white/10 to-white/5 rounded-xl sm:rounded-2xl shadow-2xl backdrop-blur-xl border border-white/20 p-4 sm:p-6 md:p-8">
              {activeTab === 'profile' && <UserProfile userData={userData} onUserUpdate={fetchUserData} />}
              {activeTab === 'add-entity' && <AddEntity userData={userData} />}
              {activeTab === 'my-businesses' && <MyBusinesses />}
              {activeTab === 'claim-business' && <ClaimBusiness />}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
